import { createSlice } from '@reduxjs/toolkit';

const defaultForm = {
  applicant: { consultants: [] },
  job: {
    is_agree: false,
    start_date: null,
    end_date: null,
    booked_data: {
      type: 'ad_hoc',       // instead of false
      period: null,
      dates: [],
      day_shifts: [],
      pay_rates: [],
      summary: '',
      levy: { enabled: true, value: 1.5 },
    },
    files: [],
    notes: '',
  },
  client: {},
  placement_external_id: null,
  edited_by: null,
  edited_by_user: null,
  document_status: null,
  id: null,
  status: null,
};

const normalizeForm = (raw = {}) => ({
  ...defaultForm,
  ...raw,
  applicant: { ...defaultForm.applicant, ...(raw.applicant ?? {}) },
  job: {
    ...defaultForm.job,
    ...(raw.job ?? {}),
    booked_data: {
      ...defaultForm.job.booked_data,
      ...((raw.job ?? {}).booked_data ?? {}),
      levy: {
        ...defaultForm.job.booked_data.levy,
        ...(((raw.job ?? {}).booked_data ?? {}).levy ?? {}),
      },
      dates: ((raw.job?.booked_data?.dates ?? [])).slice(),
      day_shifts: ((raw.job?.booked_data?.day_shifts ?? [])).slice(),
      pay_rates: ((raw.job?.booked_data?.pay_rates ?? [])).slice(),
    },
    files: (raw.job?.files ?? []).slice(),
    notes: raw.job?.notes ?? '',
  },
  client: raw.client ?? {},
});

const initialState = {
  formData: defaultForm,     // SAFE by default
  datesToConfirm: [],
  adHocDates: [],
  confirmed: false,
  workHistory: [],
  isHoliday: false,
  files: [],
  notes: '',
  levyValue: 1.5,
  levyEnabled: true,
  editedId: null,
  dataType: 'ad_hoc',        // not false
  datesForBooking: [],
};

// tiny helper to ensure deep paths exist when reducers mutate
const ensureFormPaths = (state) => {
  state.formData ??= defaultForm;
  state.formData.job ??= { ...defaultForm.job };
  state.formData.job.booked_data ??= { ...defaultForm.job.booked_data };
};

const bookingFormSlice = createSlice({
  name: 'bookingForm',
  initialState,
  reducers: {
    addFormData: (state, action) => {
      const incoming = action.payload ?? {};
      // Pre-merge each subtree so missing fields are preserved
      const merged = {
        ...state.formData,
        ...incoming,
        applicant: {
          ...(state.formData?.applicant ?? {}),
          ...(incoming.applicant ?? {}),
        },
        job: {
          ...(state.formData?.job ?? {}),
          ...(incoming.job ?? {}),
        },
        client: {
          ...(state.formData?.client ?? {}),
          ...(incoming.client ?? {}),
        },
      };
      state.formData = normalizeForm(merged, state.formData);
    },
    addApplicant: (state, action) => {
      state.formData = normalizeForm({ ...state.formData, applicant: action.payload });
    },
    addJob: (state, action) => {
      state.formData = normalizeForm({ ...state.formData, job: action.payload });
    },
    addClient: (state, action) => {
      state.formData = normalizeForm({ ...state.formData, client: action.payload });
    },
    addWorkHistory: (state, action) => {
      state.workHistory = action.payload ?? [];
    },

    changeType: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.booked_data.type = action.payload;
    },
    changeStartDate: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.start_date = action.payload;
    },
    changeEndDate: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.end_date = action.payload;
    },
    changePeriod: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.booked_data.period = action.payload;
    },
    changeRate: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.booked_data.pay_rates = action.payload ?? [];
    },
    addDate: (state, action) => {
      ensureFormPaths(state);
      console.log('reducer.addDate payload', action.payload);
      state.formData.job.booked_data.dates = action.payload ?? [];
      console.log('Submitting dates:', state.formData.job.booked_data.dates);
    },
    addShift: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.booked_data.day_shifts = action.payload ?? [];
    },
    setSummary: (state, action) => {
      ensureFormPaths(state);
      state.formData.job.booked_data.summary = action.payload ?? '';
    },

    setDatesToConfirm: (state, action) => { state.datesToConfirm = action.payload ?? []; },
    setAdHocDates: (state, action) => { state.adHocDates = action.payload ?? []; },
    setIsConfirmed: (state, action) => { state.confirmed = !!action.payload; },
    setIsHoliday: (state, action) => { state.isHoliday = !!action.payload; },
    setNewFiles: (state, action) => { state.files = action.payload ?? []; },
    setNewNotes: (state, action) => { state.notes = action.payload ?? ''; },
    setLevyEnabled: (state, action) => { state.levyEnabled = !!action.payload; },
    setLevyValue: (state, action) => { state.levyValue = Number(action.payload ?? 1.5); },
    setEditedId: (state, action) => { state.editedId = action.payload ?? null; },
    saveDataType: (state, action) => { state.dataType = action.payload ?? 'ad_hoc'; },
    saveDatesForBooking: (state, action) => { state.datesForBooking = action.payload ?? []; },
    // SECURITY: Reset form to default state when creating a new form
    // This prevents old form data from persisting when navigating to a new form
    resetForm: (state) => {
      state.formData = { ...defaultForm };
      state.datesToConfirm = [];
      state.adHocDates = [];
      state.confirmed = false;
      state.workHistory = [];
      state.isHoliday = false;
      state.files = [];
      state.notes = '';
      state.levyValue = 1.5;
      state.levyEnabled = true;
      state.editedId = null;
      state.dataType = 'ad_hoc';
      state.datesForBooking = [];
    },
  },
});

export const {
  addFormData,
  addApplicant,
  addJob,
  addClient,
  addWorkHistory,
  changeType,
  changeStartDate,
  changeEndDate,
  changePeriod,
  changeRate,
  addDate,
  addShift,
  setDatesToConfirm,
  setAdHocDates,
  setIsConfirmed,
  setSummary,
  setIsHoliday,
  setNewFiles,
  setNewNotes,
  setLevyEnabled,
  setLevyValue,
  setEditedId,
  saveDataType,
  saveDatesForBooking,
  resetForm,
} = bookingFormSlice.actions;

export default bookingFormSlice.reducer;
